'use client'

import { useHandleTopCityBookNow } from '@/src/utils/navigation/handleTopCityBookNow'
import { useHomeSearchPrefill } from '@/src/utils/navigation/useHomeSearchPrefill'
import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'

import topCitiesAndRoutesData from '@/src/constants/topCitiesAndRoutes.json'

import { TopCitiesAndRoutes } from './topCitiesAndRoutes'

interface TopCitiesAndRoutesData {
    title: string
    title_ar?: string
    routes: Array<{
        id: string
        destination_en: string
        destination_ar?: string
        imageUrl: string
        routes: Array<{
            origin_en: string
            origin_ar?: string
            destination_en: string
            destination_ar?: string
            from_code?: string
            to_code?: string
            href_en: string
            href_ar?: string
        }>
    }>
    familyTrips: {
        bannerImage: string
        title: string
        subtitle: string
        subtitleSuffix: string
        overlayTitle: string
        overlayDescription: string
        bookNowHref: string
        destinations: Array<{
            city_en: string
            city_ar?: string
            country_en: string
            country_ar?: string
            href_en: string
            href_ar?: string
        }>
    }
}

export const TopCitiesAndRoutesWrapper: React.FC = () => {
    const t = useTranslations('homePage.headings')
    const locale = useLocale()
    const data = topCitiesAndRoutesData as unknown as TopCitiesAndRoutesData
    const isArabic = locale === 'ar'
    const { handleBookNow } = useHandleTopCityBookNow()
    const { handlePrefillFromHref } = useHomeSearchPrefill({
        onBothParams: (from, to) => handleBookNow(from, to)
    })

    if (!data || !data.routes || data.routes.length === 0) {
        return null
    }

    const routes = useMemo(
        () =>
            data.routes.map(cityRoute => ({
                ...cityRoute,
                destination: isArabic ? cityRoute.destination_ar || cityRoute.destination_en : cityRoute.destination_en,
                routes: cityRoute.routes.map(route => ({
                    ...route,
                    origin: isArabic ? route.origin_ar || route.origin_en : route.origin_en,
                    destination: isArabic ? route.destination_ar || route.destination_en : route.destination_en,
                    href: isArabic ? route.href_ar || route.href_en : route.href_en,
                    fromCode: route.from_code,
                    toCode: route.to_code
                }))
            })),
        [data.routes, isArabic]
    )

    const familyTrips = useMemo(
        () => ({
            ...data.familyTrips,
            destinations: data.familyTrips.destinations.map(dest => ({
                ...dest,
                city: isArabic ? dest.city_ar || dest.city_en : dest.city_en,
                country: isArabic ? dest.country_ar || dest.country_en : dest.country_en,
                href: isArabic ? dest.href_ar || dest.href_en : dest.href_en
            }))
        }),
        [data.familyTrips, isArabic]
    )

    const handleBookClick = useCallback(
        (href: string, origin?: string, destination?: string, fromCode?: string, toCode?: string) => {
            // If origin and destination are provided, use Book Now with codes from JSON (no API call)
            if (origin && destination) {
                handleBookNow(origin, destination, fromCode, toCode)
                return
            }

            // If only one of from/to is present, prefill the homepage search form
            if (handlePrefillFromHref(href)) {
                return
            }

            // Fallback to original behavior for other cases
            window.open(href, '_blank')
        },
        [handleBookNow, handlePrefillFromHref]
    )

    return (
        <TopCitiesAndRoutes
            title={isArabic ? data.title_ar || data.title : data.title}
            routes={routes}
            familyTrips={familyTrips}
            onBookClick={handleBookClick}
            onPrefillFromHref={handlePrefillFromHref}
            t={t}
        />
    )
}

export default TopCitiesAndRoutesWrapper
